// src/context/BookEstimate.js
import { createContext, useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { wireo, plastikol } from '../Pages/Est/Calcs'; // adjust path

export const BookEstimate = createContext();

export const BookEstimateProvider = ({ children }) => {
    const [bookinfo, setBookinfo] = useState({
        length: 11,
        width: 8.5,
        spine: 0,
        textpages: 0,
        textthickness: 0,
        coverpages: 0,
        coverthickness: 0,
        otherpages: 0,
        otherthickness: 0,
        tabs: 0,
        tabsthickness: 0.01,
        additionalthickness: 0.125,
        slipsheet: false,
        collatepu: 0,
        insert: 0,
        cuts: 0,
        punchmr: 50,
        coilmr: 50,
        holes: 0,
        totalpages: 0,
        thickness: 0,
    });

    const [binding, setBinding] = useState({ type: 'Wire-O' });
    const [coilinfo, setCoilinfo] = useState({});
    const [items, setItems] = useState([]);
    const [reset, setReset] = useState(false);
    const quanbox = useRef();

    const safeNum = (val, fallback = 0) => Number(val) || fallback;

    // Calculation Functions
    const calcPunching = (item) => {
        // Placeholder - expand later
        return {
            text: { punchtotal: 0 },
            covers: { punchtotal: 0 },
            other: { punchtotal: 0 },
            tabs: { punchtotal: 0 },
        };
    };

    const calcCoilLabor = (item) => {
        const qty = safeNum(item.quantity);
        const hours = qty / 120;
        const rate = 25;
        return { coilingtotal: hours * rate };
    };

    const calcCoiling = (item) => {
        if (!coilinfo || !bookinfo.holes) {
            return { coilloops: 0, coiltotal: 0, usage: 0, numboxes: 0, rollcost: 0 };
        }

        const qty = safeNum(item.quantity);
        const holesPerBook = safeNum(bookinfo.holes);
        const totalLoops = qty * holesPerBook;

        const useBoxes = item.coiling?.coiltype === 'Boxes';

        let usage = 0;
        let materialCost = 0;
        
    console.log('Coiling calc:', { coilinfo, totalLoops, materialCost });
        if (useBoxes) {
            const loopsPerBox = safeNum(coilinfo.boxquantity, 100);
            const boxCost = safeNum(coilinfo.boxcost, 24.00);
            usage = Math.ceil(totalLoops / loopsPerBox);
            materialCost = usage * boxCost;
        } else {
            const loopsPerSpool = safeNum(coilinfo.loopsperroll, 30000);
            const spoolCost = safeNum(coilinfo.spoolcost, 124.00);
            usage = Math.ceil(totalLoops / loopsPerSpool);
            materialCost = usage * spoolCost;
        }

        return {
            coilloops: totalLoops,
            coiltotal: materialCost,
            usage,
            numboxes: useBoxes ? usage : 0,
            rollcost: materialCost,
        };

    };

    // Placeholder for others
    const calcCollate = (item) => ({ cost: 0 });
    const calcInsert = (item) => ({ cost: 0 });
    const calcTabs = (item) => ({ cost: 0 });
    const calcSlipsheets = (item) => ({ cost: 0 });
    const calcCuts = (item) => ({ cost: 0 });
    const calcBoxes = (item) => ({ cost: 0, needed: 0 });

    // Add quantity
    const checkquans = (e) => {
        if (e.key === 'Enter' && e.target.value.trim()) {
            e.preventDefault();
            const qty = e.target.value.trim();
            if (!items.some(i => i.quantity === qty)) {
                const isLarge = parseInt(qty, 10) >= 1000;
                const basePunch = {
                    punchtype: isLarge ? 'Auto' : 'Hand',
                    punchspeed: isLarge ? 7500 : 3500,
                    punchperhour: 25,
                };
                const newItem = {
                    id: uuidv4(),
                    quantity: qty,
                    punching: {
                        text: { ...basePunch },
                        covers: { ...basePunch },
                        other: { ...basePunch },
                        tabs: { ...basePunch },
                    },
                    coil: { coilinghour: 120, coilingperhour: 25, coiltype: 'Roll' }, // default to Roll
                    collate: { perpack: 0.09, perhr: 25, perpackperhour: 650 },
                    insert: { perinsert: 0.06 },
                    tabs: { per: 0.09 },
                    slipsheets: { per: 0.09 },
                    cuts: { percut: 10 },
                    boxes: { boxcost: 1.50, boxholds: 1296 },
                };
                setItems(prev => [...prev, newItem].sort((a, b) => +a.quantity - +b.quantity));
                setReset(r => !r);
            }
            e.target.value = '';
        }
    };

    const changecoilinfo = (decimal) => {
        if (!decimal) {
            setCoilinfo({});
            setReset(r => !r);
            return;
        }
        const value = parseFloat(decimal);
        const list = binding.type === 'Plastikol' ? plastikol : wireo;
        const found = list.find(c => c.decimal === value) || list.find(c => c.decimal >= value) || list[0];
        setCoilinfo(found || {});
        setReset(r => !r);
    };

    useEffect(() => {
        const text = (safeNum(bookinfo.textpages) / 2) * safeNum(bookinfo.textthickness);
        const cover = safeNum(bookinfo.coverpages) * safeNum(bookinfo.coverthickness);
        const other = (safeNum(bookinfo.otherpages) / 2) * safeNum(bookinfo.otherthickness);
        const tabsThick = safeNum(bookinfo.tabs) * safeNum(bookinfo.tabsthickness);
        const add = safeNum(bookinfo.additionalthickness);
        const thickness = Number((text + cover + other + tabsThick + add).toFixed(4));
        const totalpages = safeNum(bookinfo.textpages) + safeNum(bookinfo.coverpages) +
            safeNum(bookinfo.otherpages) + safeNum(bookinfo.tabs);
        setBookinfo(prev => ({ ...prev, thickness, totalpages }));
        setReset(r => !r);
    }, [
        bookinfo.textpages, bookinfo.textthickness, bookinfo.coverpages,
        bookinfo.coverthickness, bookinfo.otherpages, bookinfo.otherthickness,
        bookinfo.tabs, bookinfo.tabsthickness, bookinfo.additionalthickness
    ]);

    useEffect(() => {
        if (!bookinfo.thickness || !binding.type) return;
        const thickness = safeNum(bookinfo.thickness);
        const list = binding.type === 'Plastikol' ? plastikol : wireo;
        const match = list.find(c => Math.abs(c.decimal - thickness) < 0.001) ||
            list.find(c => c.decimal >= thickness) || list[0];
        setCoilinfo(match || {});
        setReset(r => !r);
    }, [bookinfo.thickness, binding.type]);

    useEffect(() => {
        if (bookinfo.spine && coilinfo.pitch) {
            setBookinfo(prev => ({
                ...prev,
                holes: Math.round(safeNum(bookinfo.spine) * safeNum(coilinfo.pitch) - 1),
            }));
        }
    }, [bookinfo.spine, coilinfo.pitch]);

    // === RECALCULATION ===
    useEffect(() => {
        if (items.length === 0) return;

        const recalculated = items.map(item => ({
            ...item,
            punching: calcPunching(item),
            coilLabor: calcCoilLabor(item),
            coiling: calcCoiling(item),
            collate: calcCollate(item),
            insert: calcInsert(item),
            tabs: calcTabs(item),
            slipsheets: calcSlipsheets(item),
            cuts: calcCuts(item),
            boxes: calcBoxes(item),
        }));

        setItems(recalculated);
    }, [reset, bookinfo, coilinfo]);

    const getlabor = (item) => {
        if (!item) return 0;
        return (
            safeNum(item.punching?.text?.punchtotal) +
            safeNum(item.punching?.covers?.punchtotal) +
            safeNum(item.punching?.other?.punchtotal) +
            safeNum(item.punching?.tabs?.punchtotal) +
            safeNum(item.coilLabor?.coilingtotal) +
            safeNum(bookinfo.punchmr) +
            safeNum(bookinfo.coilmr) +
            safeNum(item.collate?.cost) +
            safeNum(item.insert?.cost) +
            safeNum(item.cuts?.cost)
        );
    };

    const getmat = (item) => {
        if (!item) return 0;
        return (
            safeNum(item.coiling?.coiltotal) +
            safeNum(item.tabs?.cost) +
            safeNum(item.slipsheets?.cost) +
            safeNum(item.boxes?.cost)
        );
    };

    const value = {
        quanbox,
        checkquans,
        items,
        setItems,
        bookinfo,
        setBookinfo,
        coilinfo,
        setCoilinfo,
        binding,
        setBinding,
        getlabor,
        getmat,
        gettotlabor: getlabor,
        gettotmat: getmat,
        changecoilinfo,
        reset,
        setReset,
    };

    return <BookEstimate.Provider value={value}>{children}</BookEstimate.Provider>;
};